import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  BarChart3, CalendarDays, CircleHelp, FileText, Inbox, Layers, LayoutDashboard,
  LogOut, Menu, Package, PoundSterling, Receipt, Settings, UserCog, Users, X,
} from "lucide-react";
import { usePortalAuth } from "@/components/portal/PortalAuth";
import { PortalTour } from "@/components/portal/PortalTour";

const NAV = [
  { to: "/portal", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/portal/customers", label: "Customers", icon: Users },
  { to: "/portal/orders", label: "Orders", icon: Package },
  { to: "/portal/schedule", label: "Visits & Installs", icon: CalendarDays },
];
const OFFICE_NAV = [
  { to: "/portal/leads", label: "Leads", icon: Inbox },
  { to: "/portal/quotes", label: "Quotes", icon: FileText },
  { to: "/portal/payments", label: "Payments", icon: PoundSterling },
  { to: "/portal/invoices", label: "Invoices", icon: Receipt },
  { to: "/portal/materials", label: "Materials", icon: Layers },
  { to: "/portal/reports", label: "Reports", icon: BarChart3, admin: true },
  { to: "/portal/users", label: "Team", icon: UserCog, admin: true },
  { to: "/portal/settings", label: "Settings", icon: Settings, admin: true },
];
const ALL_NAV = [...NAV, ...OFFICE_NAV];

function NavItems({ onNavigate }) {
  const { user } = usePortalAuth();
  const isAdmin = user && (user.role === "admin" || user.role === "office");
  const linkCls = ({ isActive }) =>
    `flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm font-medium transition-colors ${
      isActive ? "bg-white/10 text-white" : "text-white/55 hover:text-white hover:bg-white/5"
    }`;
  return (
    <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-0.5" aria-label="Portal modules">
      {NAV.map((n) => (
        <NavLink key={n.to} to={n.to} end={n.end} data-testid={`sidebar-nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`} onClick={onNavigate} className={linkCls}>
          <n.icon className="h-4 w-4 shrink-0" />
          {n.label}
        </NavLink>
      ))}
      {isAdmin && (
        <>
          <p className="px-3.5 pt-4 pb-1.5 text-[10px] font-bold uppercase tracking-widest text-white/30">Office tools</p>
          {OFFICE_NAV.filter((n) => !n.admin || isAdmin).map((n) => (
            <NavLink key={n.to} to={n.to} data-testid={`sidebar-nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`} onClick={onNavigate} className={linkCls}>
              <n.icon className="h-4 w-4 shrink-0" />
              {n.label}
            </NavLink>
          ))}
        </>
      )}
    </nav>
  );
}

export default function PortalLayout() {
  const { user, logout } = usePortalAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [navOpen, setNavOpen] = useState(false);
  const [tourOpen, setTourOpen] = useState(false);

  useEffect(() => {
    if (user === false) navigate("/portal/login", { replace: true });
    else if (user && user.role === "installer") navigate("/portal/jobs", { replace: true });
  }, [user, navigate]);

  useEffect(() => setNavOpen(false), [location.pathname]);

  // First sign-in: auto-start the welcome tour (once per user per browser)
  useEffect(() => {
    if (user && user.role !== "installer" && !localStorage.getItem(`mbc_tour_done_${user.email}`)) {
      const t = setTimeout(() => setTourOpen(true), 900);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [user]);

  const closeTour = () => {
    if (user) localStorage.setItem(`mbc_tour_done_${user.email}`, "1");
    setTourOpen(false);
  };

  if (user === null)
    return (
      <div className="min-h-screen bg-warm-50 flex items-center justify-center">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-navy-900 border-t-transparent" />
      </div>
    );
  if (user === false || user.role === "installer") return null;

  const current = [...ALL_NAV].reverse().find((n) =>
    n.end ? location.pathname === n.to : location.pathname.startsWith(n.to));
  const title = current?.label || "Portal";

  const brand = (
    <div className="p-5 border-b border-white/10 flex items-center gap-3">
      <img src="/logo.png" alt="My Blinds Company logo" className="h-9 w-auto rounded bg-white p-0.5" />
      <div>
        <p className="font-serif text-lg leading-none text-white">Team Portal</p>
        <p className="text-[11px] text-white/40 mt-1">Business operations</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-warm-50" data-testid="portal-layout">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex fixed left-0 top-0 bottom-0 w-64 bg-navy-950 text-white z-40 flex-col border-r border-navy-800">
        {brand}
        <NavItems />
        <div className="p-4 border-t border-white/10">
          <p className="text-xs font-semibold text-white truncate">{user.name || user.email}</p>
          <p className="text-[11px] text-white/40 capitalize mb-3">{user.role}</p>
          <button
            type="button"
            data-testid="portal-logout-btn"
            onClick={logout}
            className="w-full inline-flex items-center justify-center gap-1.5 rounded-full border border-white/20 px-4 py-2 text-xs font-medium hover:bg-white/10 transition-colors"
          >
            <LogOut className="h-3.5 w-3.5" /> Sign out
          </button>
        </div>
      </aside>

      {/* Mobile slide-over sidebar */}
      {navOpen && (
        <div className="lg:hidden fixed inset-0 z-50" role="dialog" aria-label="Portal navigation">
          <button aria-label="Close menu" className="absolute inset-0 bg-navy-950/50 backdrop-blur-sm" onClick={() => setNavOpen(false)} />
          <aside className="absolute left-0 top-0 bottom-0 w-72 bg-navy-950 text-white flex flex-col shadow-2xl">
            <div className="flex items-center justify-between pr-3">
              {brand}
              <button type="button" aria-label="Close menu" data-testid="sidebar-close" onClick={() => setNavOpen(false)} className="rounded-full border border-white/20 p-1.5 text-white/70">
                <X className="h-4 w-4" />
              </button>
            </div>
            <NavItems onNavigate={() => setNavOpen(false)} />
          </aside>
        </div>
      )}

      {/* Top bar */}
      <header className="sticky top-0 z-30 h-16 bg-warm-50/90 backdrop-blur-md border-b border-warm-200 lg:pl-64">
        <div className="h-full px-4 sm:px-6 flex items-center gap-3">
          <button
            type="button"
            data-testid="sidebar-open"
            aria-label="Open menu"
            onClick={() => setNavOpen(true)}
            className="lg:hidden rounded-lg border border-warm-300 p-2 text-navy-900"
          >
            <Menu className="h-4 w-4" />
          </button>
          <h1 className="font-serif text-xl text-navy-900" data-testid="topbar-title">{title}</h1>
          <div className="ml-auto flex items-center gap-3">
            <span className="hidden sm:inline-flex items-center gap-2 rounded-full border border-warm-300 bg-white px-3.5 py-1.5 text-xs" data-testid="topbar-user">
              <span className="font-semibold text-navy-900">{user.name || user.email}</span>
              <span className="rounded-full bg-navy-900 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">{user.role}</span>
            </span>
            <button
              type="button"
              data-testid="tour-replay"
              onClick={() => setTourOpen(true)}
              aria-label="Replay the portal tour"
              title="Replay the portal tour"
              className="rounded-lg border border-warm-300 p-2 text-slate-500 hover:text-navy-900 transition-colors"
            >
              <CircleHelp className="h-4 w-4" />
            </button>
            <button
              type="button"
              data-testid="topbar-logout"
              onClick={logout}
              aria-label="Sign out"
              className="rounded-lg border border-warm-300 p-2 text-slate-500 hover:text-navy-900 transition-colors"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </header>

      <main className="lg:pl-64">
        <div className="px-4 sm:px-6 lg:px-8 py-6 max-w-7xl mx-auto">
          <Outlet />
        </div>
      </main>

      <PortalTour user={user} open={tourOpen} onClose={closeTour} />
    </div>
  );
}
